import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Autocomplete,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { processAction, getItemApptStatuses } from 'src/services/WorkflowService'
import {
  getInvoiceTypeCodeOptions,
  getReferralSourceOptions,
  getReferralContactOptions,
  getMedicalProviderOptions,
} from 'src/services/CaseService'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { PatientInvoiceDetailDTO } from 'src/viewModels/workflow/PatientInvoiceDetailDTO'
import { TypeCodeOption } from 'src/viewModels/workflow/TypeCodeOption'
import { ReferralSourceOption } from 'src/viewModels/workflow/ReferralSourceOption'
import { ReferralSourceContactOption } from 'src/viewModels/workflow/ReferralContactOption'
import { MedicalProviderOption } from 'src/viewModels/workflow/MedicalProviderOption'
import { CaseAppointmentStatusOption } from '../../viewModels/workflow/CaseAppointmentStatusOption'

interface IPatientInvoiceDetailDTOProps {
  closeModal: () => void
  isOpen: boolean
  inboxItemId: number
  inboxId: string
  action: InboxActionSummary
  data: ObjectiveProcessAction
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  associatedItemId?: string
}

function CreateCaseDetailItemModal(props: IPatientInvoiceDetailDTOProps) {
  const isAddModal = true
  const [loading, setLoading] = useState(false)
  const [typeCodeOptions, setTypeCodeOptions] = useState<TypeCodeOption[]>([])
  const [referralSourceOptions, setReferralSourceOptions] = useState<
    ReferralSourceOption[]
  >([])
  const [referralSourceContactOptions, setReferralSourceContactOptions] = useState<
    ReferralSourceContactOption[]
  >([])
  const [providerOptions, setProviderOptions] = useState<MedicalProviderOption[]>([])
  const [caseApptStatusOption, setCaseApptStatuses] = useState<
    CaseAppointmentStatusOption[]
  >([])
  const activeStatus = caseApptStatusOption.find((opt) => opt.name === 'Active')

  useEffect(() => {
    const fetchData = async () => {
      const options = await getInvoiceTypeCodeOptions()
      setTypeCodeOptions(options || [])
      const SourceOptions = await getReferralSourceOptions()
      const enabledSources = (SourceOptions || []).filter((opt) => opt.isEnabled)

      const trimmedSources = enabledSources.map((source) => ({
        ...source,
        description: source.description.trim(),
      }))

      const uniqueSources = trimmedSources.filter(
        (source, index, self) =>
          index === self.findIndex((s) => s.description === source.description)
      )

      uniqueSources.sort((a, b) => a.description.localeCompare(b.description))

      setReferralSourceOptions(uniqueSources)
      const ProviderOption = await getMedicalProviderOptions()
      setProviderOptions(ProviderOption || [])
      const caseApptStatusOptions = await getItemApptStatuses()
      setCaseApptStatuses(caseApptStatusOptions || [])
    }
    fetchData()
  }, [])

  const handlesOnClose = () => {
    props.closeModal()
  }

  const validationSchema = Yup.object({
    invoiceNumber: Yup.number()
      .typeError('Invoice # must be a number')
      .integer('Invoice # must be an integer')
      .max(2147483647, 'Invoice # must be less than 2,147,483,647'),
    dos: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null) {
          return null
        }
        return new Date(value)
      })
      .test('is-valid-date', 'Date of Service must be a valid date', () => {
        const dateInput = document.getElementById('dos') as HTMLInputElement | null

        if (dateInput && dateInput.value === '') {
          if (dateInput.validity.badInput) {
            return false
          }
        } else {
          return true
        }

        return true
      })
      .test(
        'valid-dos-date',
        'Date of Service must be on or after January 1, 2000',
        function (value) {
          if (value === null || value === undefined) return true
          return value >= new Date('2000-01-01')
        }
      ),
    invoiceDate: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null) {
          return null
        }
        return new Date(value)
      })
      .test('is-valid-date', 'Invoice Date must be a valid date', () => {
        const dateInput = document.getElementById(
          'invoiceDate'
        ) as HTMLInputElement | null

        if (dateInput && dateInput.value === '') {
          if (dateInput.validity.badInput) {
            return false
          }
        } else {
          return true
        }

        return true
      })
      .test(
        'valid-invoice-date',
        'Invoice Date must be on or after January 1, 2000',
        function (value) {
          if (value === null || value === undefined) return true
          return value >= new Date('2000-01-01')
        }
      )
      .test(
        'invoice-date-required-with-number',
        'Invoice Date is required when Invoice Number is entered',
        function (value) {
          const invoiceNumber = this.parent.invoiceNumber
          if (invoiceNumber && invoiceNumber !== '') {
            return value !== null && value !== undefined
          }
          return true
        }
      ),
    dateForRush: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null) {
          return null
        }
        return new Date(value)
      })
      .test('is-valid-date', 'Date For Rush must be a valid date', () => {
        const dateInput = document.getElementById(
          'dateForRush'
        ) as HTMLInputElement | null

        if (dateInput && dateInput.value === '') {
          if (dateInput.validity.badInput) {
            return false
          }
        } else {
          return true
        }

        return true
      })
      .test(
        'valid-rush-date',
        'Date For Rush must be on or after January 1, 2000',
        function (value) {
          if (value === null || value === undefined) return true
          return value >= new Date('2000-01-01')
        }
      ),
    typeCode: Yup.string(),
    providerId: Yup.number(),
    numberOfHours: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .notRequired()
      .min(0, 'Number of Hours must be greater than or equal to 0'),
    billingPricePerHour: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .notRequired()
      .min(0, 'Price Per Hour must be greater than or equal to 0'),
    invoiceAmount: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .notRequired(),
    invoiceSpecificText: Yup.string(),
    referralSource: Yup.string(),
    comments: Yup.string().when('requireComment', {
      is: false,
      then: (schema: any) => schema.notRequired(),
      otherwise: (schema: any) => schema.required('Comments are required'),
    }),
  })

  async function processCaseDetailItem(values: any) {
    setLoading(true)

    let data = new ObjectiveProcessAction()

    const createLineDetailRequest: PatientInvoiceDetailDTO = {
      invoiceNumber:
        values.invoiceNumber === '' ? undefined : Number(values.invoiceNumber),
      groupNumber: values.groupNumber === '' ? undefined : Number(values.groupNumber),
      dos: values.dos || undefined,
      invoiceDate: values.invoiceDate || undefined,
      dateForRush: values.dateForRush || undefined,
      rsId: values.rsId === '' ? undefined : Number(values.rsId),
      rscontactId: values.rscontactId === '' ? undefined : Number(values.rscontactId),
      apptStatus: values.apptStatus === '' ? undefined : values.apptStatus,
      typeCode: values.typeCode || undefined,
      providerId: values.providerId === '' ? undefined : Number(values.providerId),
      numberOfHours:
        values.numberOfHours === '' ? undefined : Number(values.numberOfHours),
      billingPricePerHour:
        values.billingPricePerHour === ''
          ? undefined
          : Number(values.billingPricePerHour),
      invoiceAmount:
        values.invoiceAmount === '' ? undefined : Number(values.invoiceAmount),
      invoiceSpecificText: values.invoiceSpecificText || undefined,
    }

    data.lineDetailRequest = createLineDetailRequest

    if (values.comments) {
      data.comment = values.comments
    }

    processAction(data, props.action.id.toString(), props.inboxItemId.toString())
      .then(() => {
        props.handleProcessActionFinish!(props.action)
        setLoading(false)
        props.closeModal()
      })
      .catch(() => {
        setLoading(false)
        props.closeModal()
      })
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      open={props.isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handlesOnClose()
        }
      }}
    >
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar title={props.action.actionName} isModal={true} />
      </DialogTitle>
      <DialogContent style={{ padding: '12px 12px 8px' }}>
        {loading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1200',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Formik
          initialValues={{
            invoiceNumber: undefined,
            dos: undefined,
            invoiceDate: undefined,
            dateForRush: undefined,
            rsId: undefined,
            rscontactId: undefined,
            apptStatus: activeStatus ? activeStatus.id : '',
            typeCode: undefined,
            providerId: undefined,
            numberOfHours: undefined,
            billingPricePerHour: undefined,
            invoiceAmount: undefined,
            invoiceSpecificText: undefined,
            comments: '',
            requireComment: props.action.requireComment,
            referralSource: undefined,
          }}
          enableReinitialize={true}
          validationSchema={validationSchema}
          validateOnMount={true}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={processCaseDetailItem}
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            isValid,
            handleBlur,
            setFieldValue,
            validateForm,
          }) => {
            const hours = parseFloat(String(values.numberOfHours || '0'))
            const rate = parseFloat(String(values.billingPricePerHour || '0'))

            if (
              values.numberOfHours !== undefined &&
              values.numberOfHours !== '' &&
              !isNaN(hours) &&
              values.billingPricePerHour !== undefined &&
              values.billingPricePerHour !== '' &&
              !isNaN(rate)
            ) {
              const amount = Math.round(hours * rate * 100) / 100
              if (values.invoiceAmount !== amount) {
                setFieldValue('invoiceAmount', amount)
              }
            }

            return (
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <Autocomplete
                      options={typeCodeOptions}
                      getOptionLabel={(option: TypeCodeOption) =>
                        `${option.description} ($${option.pricePerHour} per hour)`
                      }
                      value={
                        typeCodeOptions.find((opt) => opt.typeCode === values.typeCode) ||
                        null
                      }
                      onChange={(_, newValue: TypeCodeOption | null) => {
                        setFieldValue('typeCode', newValue ? newValue.typeCode : '')
                        setFieldValue(
                          'billingPricePerHour',
                          newValue ? newValue.pricePerHour : ''
                        )
                        setTimeout(() => validateForm(), 0)
                      }}
                      size="small"
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="Type Code"
                          variant="outlined"
                          required
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                    />
                    {values.typeCode && (
                      <div style={{ marginTop: 4, color: '#888', fontSize: 13 }}>
                        <em>
                          {
                            typeCodeOptions.find(
                              (opt) => opt.typeCode === values.typeCode
                            )?.additionalDescription
                          }
                        </em>
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      size="small"
                      onBlur={handleBlur}
                      inputProps={{ id: 'dateForRush' }}
                      InputLabelProps={{ shrink: true }}
                      type="date"
                      name="dateForRush"
                      variant="outlined"
                      value={values.dateForRush}
                      onChange={handleChange}
                      label="Date For Rush"
                      fullWidth
                      error={!!(touched.dateForRush && errors.dateForRush)}
                      helperText={(touched.dateForRush && errors.dateForRush) as string}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <Autocomplete
                      options={referralSourceOptions}
                      getOptionLabel={(option: ReferralSourceOption) =>
                        option.description
                      }
                      value={
                        referralSourceOptions.find(
                          (opt) => opt.description === values.referralSource
                        ) || null
                      }
                      onChange={(_, newValue: ReferralSourceOption | null) => {
                        setFieldValue(
                          'referralSource',
                          newValue ? newValue.description : ''
                        )
                        setFieldValue('rsId', newValue ? newValue.id : '')
                        setFieldValue('rscontactId', '')

                        if (newValue && newValue.id) {
                          getReferralContactOptions(newValue.id).then((contacts) => {
                            setReferralSourceContactOptions(contacts || [])
                          })
                        } else {
                          setReferralSourceContactOptions([])
                        }

                        setTimeout(() => validateForm(), 0)
                      }}
                      size="small"
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="Referral Source"
                          variant="outlined"
                          required
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          error={!!(touched.referralSource && errors.referralSource)}
                          helperText={touched.referralSource && errors.referralSource}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Autocomplete
                      options={referralSourceContactOptions}
                      getOptionLabel={(option: ReferralSourceContactOption) =>
                        option.description
                      }
                      value={
                        referralSourceContactOptions.find(
                          (contact) => contact.id === values.rscontactId
                        ) || null
                      }
                      onChange={(_, newValue: ReferralSourceContactOption | null) => {
                        setFieldValue('rscontactId', newValue ? newValue.id : '')
                      }}
                      size="small"
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="Referral Contact"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Autocomplete
                      options={providerOptions}
                      getOptionLabel={(option: MedicalProviderOption) =>
                        option.description
                      }
                      value={
                        providerOptions.find((opt) => opt.id === values.providerId) ||
                        null
                      }
                      onChange={(_, newValue: MedicalProviderOption | null) => {
                        setFieldValue('providerId', newValue ? newValue.id : '')
                      }}
                      size="small"
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="Medical Provider"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      size="small"
                      onBlur={handleBlur}
                      inputProps={{ id: 'dos' }}
                      InputLabelProps={{ shrink: true }}
                      type="date"
                      name="dos"
                      variant="outlined"
                      value={values.dos}
                      onChange={handleChange}
                      label="Date of Service"
                      fullWidth
                      error={!!(touched.dos && errors.dos)}
                      helperText={(touched.dos && errors.dos) as string}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Autocomplete
                      options={caseApptStatusOption.filter(
                        (opt) => opt.name === 'Active'
                      )}
                      getOptionLabel={(option: CaseAppointmentStatusOption) =>
                        option.name
                      }
                      value={
                        caseApptStatusOption.find(
                          (opt) => opt.id === values.apptStatus
                        ) || null
                      }
                      disabled={isAddModal}
                      size="small"
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="Status"
                          variant="outlined"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      size="small"
                      onBlur={handleBlur}
                      inputProps={{ id: 'invoiceNumber' }}
                      InputLabelProps={{ shrink: true }}
                      name="invoiceNumber"
                      variant="outlined"
                      value={values.invoiceNumber}
                      onChange={handleChange}
                      label="Invoice Number"
                      fullWidth
                      error={!!(touched.invoiceNumber && errors.invoiceNumber)}
                      helperText={
                        (touched.invoiceNumber && errors.invoiceNumber) as string
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      size="small"
                      onBlur={handleBlur}
                      inputProps={{ id: 'invoiceDate' }}
                      InputLabelProps={{ shrink: true }}
                      type="date"
                      name="invoiceDate"
                      variant="outlined"
                      value={values.invoiceDate}
                      onChange={handleChange}
                      label="Invoice Date"
                      fullWidth
                      error={!!(touched.invoiceDate && errors.invoiceDate)}
                      helperText={(touched.invoiceDate && errors.invoiceDate) as string}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      size="small"
                      onBlur={handleBlur}
                      inputProps={{ id: 'numberOfHours' }}
                      InputLabelProps={{ shrink: true }}
                      name="numberOfHours"
                      variant="outlined"
                      value={values.numberOfHours}
                      onChange={handleChange}
                      label="Number of Hours"
                      fullWidth
                      error={!!(touched.numberOfHours && errors.numberOfHours)}
                      helperText={
                        (touched.numberOfHours && errors.numberOfHours) as string
                      }
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      size="small"
                      onBlur={handleBlur}
                      inputProps={{ id: 'billingPricePerHour' }}
                      InputLabelProps={{ shrink: true }}
                      name="billingPricePerHour"
                      variant="outlined"
                      value={values.billingPricePerHour}
                      onChange={handleChange}
                      label="Price Per Hour"
                      fullWidth
                      error={
                        !!(touched.billingPricePerHour && errors.billingPricePerHour)
                      }
                      helperText={
                        (touched.billingPricePerHour &&
                          errors.billingPricePerHour) as string
                      }
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      size="small"
                      onBlur={handleBlur}
                      inputProps={{ id: 'invoiceAmount' }}
                      InputLabelProps={{ shrink: true }}
                      name="invoiceAmount"
                      variant="outlined"
                      value={
                        values.invoiceAmount === 0 || values.invoiceAmount === '0'
                          ? undefined
                          : values.invoiceAmount
                      }
                      onChange={handleChange}
                      label="Invoice Amount"
                      fullWidth
                      disabled={true}
                      error={!!(touched.invoiceAmount && errors.invoiceAmount)}
                      helperText={
                        (touched.invoiceAmount && errors.invoiceAmount) as string
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      onBlur={handleBlur}
                      inputProps={{ id: 'invoiceSpecificText', maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      name="invoiceSpecificText"
                      variant="outlined"
                      value={values.invoiceSpecificText}
                      onChange={handleChange}
                      label="Invoice Notes"
                      fullWidth
                      error={
                        !!(touched.invoiceSpecificText && errors.invoiceSpecificText)
                      }
                      helperText={
                        (touched.invoiceSpecificText &&
                          errors.invoiceSpecificText) as string
                      }
                    />
                  </Grid>
                  {props.action.displayComment && (
                    <Grid item xs={12}>
                      <TextField
                        onBlur={handleBlur}
                        inputProps={{ id: 'comments' }}
                        InputLabelProps={{ shrink: true }}
                        type="text"
                        name="comments"
                        variant="outlined"
                        value={values.comments}
                        onChange={handleChange}
                        label="Comments"
                        fullWidth
                        multiline
                        minRows={4}
                        required={props.action.requireComment}
                        error={!!(touched.comments && errors.comments)}
                        helperText={(touched.comments && errors.comments) as string}
                      />
                    </Grid>
                  )}
                </Grid>
                <DialogActions>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={loading || !isValid}
                  >
                    Execute
                  </Button>
                  <Button onClick={handlesOnClose} color="primary">
                    Close
                  </Button>
                </DialogActions>
              </Form>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default CreateCaseDetailItemModal
